import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useCompanyTheme } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { KnowledgeGraphTabType } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { useThemeDrawer } from '@terminal/widgets/theme-widget/theme-drawer/useThemeDrawer';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useSearchParams } from 'react-router';

import { getKnowledgeGraphSearchParams } from '../../utils/knowledge-graph-utils';
import { LoadingCell } from '../shared/loading-cell/LoadingCell';
import styles from './LegacyThemeKnowledgeGraph.module.scss';

export interface LegacyThemeKnowledgeGraphProps {
  entityTag: string;
  isInDrawer?: boolean;
}

/**
 * @deprecated
 */
export const LegacyThemeKnowledgeGraph = ({
  entityTag,
  isInDrawer = true,
}: LegacyThemeKnowledgeGraphProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { navigateToTheme } = useNavigateToThemeOverview();
  const { openDrawer } = useThemeDrawer();
  const { trackThemeOverviewDrawerOpen } = usePosthogEvents();

  const {
    data = [],
    isFetching,
    error,
  } = useCompanyTheme({
    entityTag,
    limit: 3,
  });

  if (error) {
    throw error;
  }

  const isAssetOverviewPage = matchPath(
    { path: appPaths.assetOverview, end: false, caseSensitive: false },
    location.pathname
  );

  const getTooltip = (context: string) => {
    if (isAssetOverviewPage) {
      return undefined;
    }

    /** i18next-extract-mark-context-next-line ["themeExposure"] */
    return t('widget:assetOverview:knowledgeGraphTooltipRevamp', {
      context: context,
    });
  };

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.ThemeExposure,
  }).toString();
  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;
  const onClickHandler = isInDrawer
    ? (themeId: string) => {
        const themeSearchParams = new URLSearchParams();
        themeSearchParams.set(queryKeys.activeSubTab, 'overview');
        navigateToTheme({
          themeId,
          source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
          searchParams: themeSearchParams,
        });
      }
    : (themeId: string) => {
        trackThemeOverviewDrawerOpen(POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW, [
          themeId,
        ]);
        openDrawer(themeId);
      };

  if (!data.length) {
    return null;
  }

  return (
    <div>
      <SectionTitle
        title={t('widget:assetOverview.themes')}
        variant="small"
        linkTo={linkToKnowledgeGraph}
        tooltip={getTooltip('themeExposure')}
      />
      {isFetching ? (
        <div>
          {Array.from({ length: 3 }).map((_, idx) => (
            <div className={styles.themeRow} data-theme-row key={idx}>
              <LoadingCell />
            </div>
          ))}
        </div>
      ) : (
        <div>
          {data.map(d => (
            <div
              className={styles.themeRow}
              data-theme-row
              onClick={() => onClickHandler(d.theme.id)}
              key={d.theme.name}
            >
              <div className={styles.themeNameWrapper}>
                <ThemeLogoIcon
                  name={d.theme.name}
                  logo_url={d.theme.logo_url}
                  fallbackIcon={
                    <div className={styles.logoWrapper}>
                      {d.theme.name.charAt(0)}
                    </div>
                  }
                />
                <span className={styles.themeName}>{d.theme.name}</span>
              </div>
              <TopRankIcon rank={d.relationship.rank} iconSize={28} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
